import { Ionicons } from "@expo/vector-icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
    ActivityIndicator,
    Alert,
    FlatList,
    Modal,
    Pressable,
    StyleSheet,
    Switch,
    Text,
    TextInput,
    useColorScheme,
    View,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { produtosRepository } from "../repositories/produtosRepository";
import { Produto } from "../types/database";

type ModalModo = "criar" | "editar" | null;

function parsePreco(valor: string): number | null {
  const normalizado = valor.trim().replace(",", ".");
  if (!normalizado) return null;

  const preco = parseFloat(normalizado);
  if (Number.isNaN(preco) || preco < 0) return null;

  return preco;
}

function validarFormularioProduto(
  nome: string,
  precoTexto: string,
): string | null {
  if (!nome.trim()) return "Informe o nome do produto.";
  if (parsePreco(precoTexto) === null)
    return "Informe um preço válido maior ou igual a zero.";
  return null;
}

export default function TelaProdutos() {
  const queryClient = useQueryClient();
  const colorScheme = useColorScheme();
  const isDark = colorScheme === "dark";
  const styles = createStyles(isDark);

  const [modalModo, setModalModo] = useState<ModalModo>(null);
  const [produtoEmEdicao, setProdutoEmEdicao] = useState<Produto | null>(null);
  const [nome, setNome] = useState("");
  const [precoTexto, setPrecoTexto] = useState("");
  const [produtoEmAcao, setProdutoEmAcao] = useState<string | null>(null);

  const {
    data: produtos,
    isLoading,
    error,
    refetch,
    isRefetching,
  } = useQuery({
    queryKey: ["produtos", "gerenciamento"],
    queryFn: produtosRepository.buscarTodosGerenciamento,
  });

  function invalidarProdutos() {
    queryClient.invalidateQueries({ queryKey: ["produtos"] });
  }

  const criarMutation = useMutation({
    mutationFn: ({
      nomeProduto,
      preco,
    }: {
      nomeProduto: string;
      preco: number;
    }) => produtosRepository.criarProduto(nomeProduto, preco),
    onSuccess: () => {
      invalidarProdutos();
      fecharModal();
      Alert.alert("Sucesso", "Produto criado com sucesso!");
    },
    onError: (err: Error) => {
      Alert.alert("Erro", err.message || "Não foi possível criar o produto.");
    },
  });

  const atualizarMutation = useMutation({
    mutationFn: ({ id, dados }: { id: string; dados: Partial<Produto> }) =>
      produtosRepository.atualizarProduto(id, dados),
    onSuccess: () => {
      invalidarProdutos();
    },
    onError: (err: Error) => {
      Alert.alert(
        "Erro",
        err.message || "Não foi possível atualizar o produto.",
      );
    },
    onSettled: () => {
      setProdutoEmAcao(null);
    },
  });

  function abrirModalCriar() {
    setProdutoEmEdicao(null);
    setNome("");
    setPrecoTexto("");
    setModalModo("criar");
  }

  function abrirModalEditar(produto: Produto) {
    setProdutoEmEdicao(produto);
    setNome(produto.nome);
    setPrecoTexto(produto.preco.toFixed(2));
    setModalModo("editar");
  }

  function fecharModal() {
    setModalModo(null);
    setProdutoEmEdicao(null);
    setNome("");
    setPrecoTexto("");
  }

  function handleSalvarModal() {
    const erro = validarFormularioProduto(nome, precoTexto);
    if (erro) {
      Alert.alert("Atenção", erro);
      return;
    }

    const preco = parsePreco(precoTexto)!;

    if (modalModo === "criar") {
      criarMutation.mutate({ nomeProduto: nome, preco });
      return;
    }

    if (modalModo === "editar" && produtoEmEdicao) {
      atualizarMutation.mutate(
        {
          id: produtoEmEdicao.id,
          dados: { nome, preco },
        },
        {
          onSuccess: () => {
            fecharModal();
            Alert.alert("Sucesso", "Produto atualizado com sucesso!");
          },
        },
      );
    }
  }

  function handleToggleAtivo(produto: Produto, ativo: boolean) {
    setProdutoEmAcao(produto.id);
    atualizarMutation.mutate({ id: produto.id, dados: { ativo } });
  }

  function renderProduto({ item }: { item: Produto }) {
    const emAcao = produtoEmAcao === item.id && atualizarMutation.isPending;

    return (
      <View style={[styles.card, !item.ativo && styles.cardInativo]}>
        <View style={styles.cardConteudo}>
          <Text
            style={[styles.produtoNome, !item.ativo && styles.textoInativo]}
          >
            {item.nome}
          </Text>
          <Text
            style={[styles.produtoPreco, !item.ativo && styles.textoInativo]}
          >
            R$ {item.preco.toFixed(2)}
          </Text>
          {!item.ativo && <Text style={styles.tagInativo}>Inativo</Text>}
        </View>

        <View style={styles.cardAcoes}>
          <View style={styles.toggleRow}>
            <Text style={styles.toggleLabel}>Ativo</Text>
            <Switch
              value={item.ativo}
              onValueChange={(ativo) => handleToggleAtivo(item, ativo)}
              disabled={emAcao}
              trackColor={{ false: "#d1d5db", true: "#93c5fd" }}
              thumbColor={item.ativo ? "#2563eb" : "#f4f4f5"}
            />
          </View>

          <Pressable
            style={styles.botaoEditar}
            onPress={() => abrirModalEditar(item)}
            disabled={emAcao}
          >
            <Ionicons name="pencil-outline" size={18} color="#2563eb" />
            <Text style={styles.botaoEditarTexto}>Editar</Text>
          </Pressable>
        </View>
      </View>
    );
  }

  const salvandoModal = criarMutation.isPending || atualizarMutation.isPending;

  if (isLoading) {
    return (
      <View style={styles.center}>
        <ActivityIndicator size="large" color="#2563eb" />
        <Text style={styles.textoCarregando}>Carregando produtos...</Text>
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.center}>
        <Text style={styles.textoErro}>Erro ao carregar produtos.</Text>
        <Pressable style={styles.botaoRetry} onPress={() => refetch()}>
          <Text style={styles.botaoRetryTexto}>Tentar novamente</Text>
        </Pressable>
      </View>
    );
  }

  return (
    <SafeAreaView style={styles.container} edges={["bottom"]}>
      <FlatList
        data={produtos}
        keyExtractor={(item) => item.id}
        renderItem={renderProduto}
        contentContainerStyle={styles.lista}
        refreshing={isRefetching}
        onRefresh={refetch}
        ListEmptyComponent={
          <View style={styles.listaVazia}>
            <Text style={styles.listaVaziaTexto}>
              Nenhum produto cadastrado.
            </Text>
          </View>
        }
      />

      <Pressable style={styles.fab} onPress={abrirModalCriar}>
        <Ionicons name="add" size={28} color="#fff" />
      </Pressable>

      <Modal
        visible={modalModo !== null}
        transparent
        animationType="slide"
        onRequestClose={fecharModal}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalConteudo}>
            <Text style={styles.modalTitulo}>
              {modalModo === "criar" ? "Novo Produto" : "Editar Produto"}
            </Text>

            <Text style={styles.label}>Nome</Text>
            <TextInput
              style={styles.input}
              value={nome}
              onChangeText={setNome}
              placeholder="Ex: Refrigerante"
              placeholderTextColor={isDark ? "#888" : "#6b7280"}
              autoCapitalize="words"
            />

            <Text style={styles.label}>Preço (R$)</Text>
            <TextInput
              style={styles.input}
              value={precoTexto}
              onChangeText={setPrecoTexto}
              placeholder="Ex: 5.50"
              placeholderTextColor={isDark ? "#888" : "#6b7280"}
              keyboardType="decimal-pad"
            />

            <View style={styles.modalAcoes}>
              <Pressable
                style={styles.botaoCancelar}
                onPress={fecharModal}
                disabled={salvandoModal}
              >
                <Text style={styles.botaoCancelarTexto}>Cancelar</Text>
              </Pressable>

              <Pressable
                style={[
                  styles.botaoSalvar,
                  salvandoModal && styles.botaoDesabilitado,
                ]}
                onPress={handleSalvarModal}
                disabled={salvandoModal}
              >
                {salvandoModal ? (
                  <ActivityIndicator color="#fff" size="small" />
                ) : (
                  <Text style={styles.botaoSalvarTexto}>Salvar</Text>
                )}
              </Pressable>
            </View>
          </View>
        </View>
      </Modal>
    </SafeAreaView>
  );
}

function createStyles(isDark: boolean) {
  return StyleSheet.create({
    container: { flex: 1, backgroundColor: isDark ? "#121212" : "#F5F5F5" },
    center: {
      flex: 1,
      justifyContent: "center",
      alignItems: "center",
      padding: 24,
      backgroundColor: isDark ? "#121212" : "#F5F5F5",
    },
    textoCarregando: { marginTop: 12, color: isDark ? "#AAAAAA" : "#666" },
    textoErro: { color: "#dc2626", marginBottom: 16 },
    botaoRetry: {
      backgroundColor: "#2563eb",
      paddingHorizontal: 20,
      paddingVertical: 10,
      borderRadius: 8,
    },
    botaoRetryTexto: { color: "#fff", fontWeight: "600" },
    lista: { padding: 20, paddingBottom: 100 },
    listaVazia: { paddingVertical: 40, alignItems: "center" },
    listaVaziaTexto: { color: isDark ? "#AAAAAA" : "#6b7280", fontSize: 16 },
    card: {
      backgroundColor: isDark ? "#1E1E1E" : "#fff",
      borderRadius: 12,
      padding: 16,
      marginBottom: 12,
      borderWidth: 1,
      borderColor: isDark ? "#2A2A2A" : "#e5e7eb",
    },
    cardInativo: {
      backgroundColor: isDark ? "#1B1B1B" : "#f9fafb",
      opacity: 0.85,
    },
    cardConteudo: { marginBottom: 12 },
    produtoNome: {
      fontSize: 18,
      fontWeight: "bold",
      color: isDark ? "#FFFFFF" : "#111",
    },
    produtoPreco: {
      fontSize: 16,
      color: isDark ? "#4ADE80" : "#059669",
      marginTop: 4,
      fontWeight: "600",
    },
    produtoCusto: {
      fontSize: 14,
      color: isDark ? "#AAAAAA" : "#6b7280",
      marginTop: 4,
      fontWeight: "500",
    },
    textoInativo: { color: isDark ? "#9CA3AF" : "#9ca3af" },
    tagInativo: {
      marginTop: 6,
      alignSelf: "flex-start",
      fontSize: 12,
      fontWeight: "600",
      color: isDark ? "#AAA" : "#6b7280",
      backgroundColor: isDark ? "#2A2A2A" : "#e5e7eb",
      paddingHorizontal: 8,
      paddingVertical: 2,
      borderRadius: 4,
    },
    cardAcoes: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      borderTopWidth: 1,
      borderTopColor: isDark ? "#2A2A2A" : "#f3f4f6",
      paddingTop: 12,
    },
    toggleRow: { flexDirection: "row", alignItems: "center", gap: 8 },
    toggleLabel: {
      fontSize: 14,
      color: isDark ? "#E5E7EB" : "#374151",
      fontWeight: "500",
    },
    botaoEditar: {
      flexDirection: "row",
      alignItems: "center",
      gap: 4,
      paddingHorizontal: 12,
      paddingVertical: 6,
      borderRadius: 6,
      backgroundColor: isDark ? "#1F2C3A" : "#eff6ff",
    },
    botaoEditarTexto: {
      color: isDark ? "#93C5FD" : "#2563eb",
      fontWeight: "600",
      fontSize: 14,
    },
    fab: {
      position: "absolute",
      right: 24,
      bottom: 24,
      width: 56,
      height: 56,
      borderRadius: 28,
      backgroundColor: "#2563eb",
      justifyContent: "center",
      alignItems: "center",
      elevation: 4,
      shadowColor: "#000",
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.25,
      shadowRadius: 4,
    },
    modalOverlay: {
      flex: 1,
      backgroundColor: "rgba(0,0,0,0.5)",
      justifyContent: "flex-end",
    },
    modalConteudo: {
      backgroundColor: isDark ? "#1E1E1E" : "#fff",
      borderTopLeftRadius: 16,
      borderTopRightRadius: 16,
      padding: 24,
      paddingBottom: 32,
    },
    modalTitulo: {
      fontSize: 20,
      fontWeight: "bold",
      marginBottom: 20,
      color: isDark ? "#FFFFFF" : "#111",
    },
    label: {
      fontSize: 14,
      fontWeight: "600",
      marginBottom: 6,
      color: isDark ? "#E5E7EB" : "#374151",
    },
    input: {
      backgroundColor: isDark ? "#2C2C2C" : "#f9fafb",
      borderWidth: 1,
      borderColor: isDark ? "#4B5563" : "#d1d5db",
      borderRadius: 8,
      paddingHorizontal: 12,
      paddingVertical: 10,
      fontSize: 16,
      marginBottom: 16,
      color: isDark ? "#FFFFFF" : "#111827",
    },
    modalAcoes: { flexDirection: "row", gap: 12, marginTop: 8 },
    botaoCancelar: {
      flex: 1,
      paddingVertical: 14,
      borderRadius: 8,
      alignItems: "center",
      backgroundColor: isDark ? "#2A2A2A" : "#f3f4f6",
    },
    botaoCancelarTexto: {
      color: isDark ? "#E5E7EB" : "#374151",
      fontWeight: "600",
      fontSize: 16,
    },
    botaoSalvar: {
      flex: 1,
      paddingVertical: 14,
      borderRadius: 8,
      alignItems: "center",
      backgroundColor: "#2563eb",
    },
    botaoSalvarTexto: { color: "#fff", fontWeight: "bold", fontSize: 16 },
    botaoDesabilitado: { opacity: 0.7 },
  });
}
